import * as React from "react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import DeleteIcon from '@mui/icons-material/Delete';
import { AXIOS_API_INSTANCE } from "../../../Constants"
import { send_authorized_request } from "../../../Constants/cognito"
import { useSnackbar } from 'notistack';
import { Box, Checkbox, Divider, FormControlLabel, IconButton, InputAdornment, Tooltip } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { LoadingBackdropIndeterminate } from "../../LoadingBackdrop"
import { DataGrid } from "@mui/x-data-grid"
import Alert from '@mui/material/Alert';
import { findLocation, useLocations } from "../../ReactQuery/QueryLocations"
import { moneyFormat, parseMoneyInput } from "../../../Utils"

const columns = [
    {
        field: 'name',
        flex: 1,
    },
    {
        field: 'description',
        flex: 1,
    },
    {
        field: 'price',
        flex: 1,
        valueGetter: (value, row) => row.inventory_item_locations[0]?.price || row.default_price
    }
]

export default function AddPartModal({
    serviceOrder,
    serviceOrderService,
    part,
    open,
    closeModal,
    addingNewPart
}) {
    const { enqueueSnackbar } = useSnackbar();
    const { data: locations, } = useLocations();
    const [myPart, setMyPart] = React.useState({ quantity: 1 })
    const [notes, setNotes] = React.useState("")
    const [saving, setSaving] = React.useState(false)
    const [isLoading, setIsLoading] = React.useState(false)
    const [searchedParts, setSearchedParts] = React.useState([])
    const [searchedPart, setSearchedPart] = React.useState(false)
    const [overrideQuotePrice, setOverrideQuotePrice] = React.useState(null)

    const handleChange = (prop) => (event, sometimes_new_value) => {
        if (['cost', 'quantity'].includes(prop)) {
            setMyPart({
                ...myPart,
                [prop]: parseMoneyInput(myPart[prop], event.target.value, { allow_empty: true }),
            })
            if (prop === 'cost' && overrideQuotePrice === null && part?.quoted_cost !== null && !addingNewPart) {
                setOverrideQuotePrice(false)
            }
        } else {
            setMyPart({
                ...myPart,
                [prop]: event.target.value,
            })
        }
    }

    const searchedPartClick = (part) => {
        console.log(myPart)
        setMyPart({
            name: part.name,
            description: part.description,
            sku: part.inventory_item_identifiers[0]?.item_identifier,
            cost: part.inventory_item_locations[0]?.price || part.default_price,
            quantity: myPart.quantity,
            source: 'INTERNAL'
        })
        setSearchedParts([])
        setSearchedPart(part)
        if (overrideQuotePrice === null && part?.quoted_cost !== null && !addingNewPart) {
            setOverrideQuotePrice(false)
        }
    }

    const searchName = () => {
        search({ 'name.ilike': myPart.name })
    }
    const searchDescription = () => {
        search({ 'description.ilike': myPart.description })
    }
    const searchSkuUpc = () => {
        if (findLocation(locations, serviceOrder.current_location_id)?.integrations?.PALADIN !== undefined) {
            setIsLoading(true)
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.post(`locations/${serviceOrder.current_location_id}/integrations/paladin/part_lookup/${myPart.sku}`).then((inventoryitem) => {
                    enqueueSnackbar('Item found', { variant: 'success' })
                    const paladin_part = {
                        name: inventoryitem.data.data.Description1,
                        description: '',
                        sku: inventoryitem.data.data.PartNumber,
                        cost: inventoryitem.data.data.RetailPrice,
                        quantity: myPart.quantity,
                        source: 'PALADIN',
                        qoh: inventoryitem.data.data.StockOnHand,
                    }
                    setMyPart(paladin_part)
                    if (overrideQuotePrice === null && part?.quoted_cost !== null && !addingNewPart) {
                        setOverrideQuotePrice(false)
                    }
                    setSearchedPart(paladin_part)
                    setIsLoading(false)
                }).catch((error) => {
                    setIsLoading(false)
                    enqueueSnackbar(`Failed to query paladin <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            )
        } else {
            search({ 'inventory_item_identifiers:item_identifier.eq': myPart.sku })
        }
    }
    React.useEffect(() => {
        if (!addingNewPart) {
            setMyPart(part)
        } else {
            setMyPart({ quantity: 1 })
        }
        setSearchedPart(false)
        setOverrideQuotePrice(null)
        setNotes("")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const search = (filter) => {
        setIsLoading(true)
        filter['inventory_item_locations:location_id.eq'] = serviceOrder.location_id
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("inventoryitems", {
                params: {
                    page_num: 0,
                    page_size: 50,
                    order_by: 'name',
                    order_by_direction: 'asc',
                    filters: JSON.stringify(filter),
                    include_location_items: true
                },
            }).then((inventoryitems) => {
                if (inventoryitems.data.data.length === 1) {
                    searchedPartClick(inventoryitems.data.data[0])
                    enqueueSnackbar('Item found', { variant: 'success' })
                } else if (inventoryitems.data.data.length === 0) {
                    enqueueSnackbar('No items found', { variant: 'warning' })
                } else {
                    setSearchedParts(inventoryitems.data.data)
                }
                setIsLoading(false)
            }).catch((error) => {
                setIsLoading(false)
                enqueueSnackbar(`Failed to search inventoryitems <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    const save = (event) => {
        setSaving(true)
        const sending_part = { ...myPart }
        delete sending_part.status_name
        delete sending_part.status_type
        delete sending_part.qoh
        const edit_part = {
            serviceorderservice_id: serviceOrderService.id,
            part_id: part.id,
            part: sending_part,
            override_quote_price: overrideQuotePrice,
            notes: notes.trim()
        }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrderService.service_order_id}/edit_part`, edit_part).then((serviceOrder) => {
                setSaving(false)
                enqueueSnackbar('Successfully updated part', { variant: 'success' })
                closeModal(serviceOrder.data.data)
            }).catch((error) => {
                setSaving(false)
                enqueueSnackbar(`Failed to update part <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    const create = (event) => {
        setSaving(true)
        const new_part = {
            serviceorderservice_id: serviceOrderService.id,
            part: myPart,
            inventory_item_id: searchedPart?.id,
            notes: notes.trim()
        }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrderService.service_order_id}/add_part`, new_part).then((serviceOrder) => {
                setSaving(false)
                enqueueSnackbar('Successfully added part', { variant: 'success' })
                closeModal(serviceOrder.data.data)
            }).catch((error) => {
                setSaving(false)
                enqueueSnackbar(`Failed to add part <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    const removePart = (event) => {
        setSaving(true)
        const remove_part = {
            serviceorderservice_id: serviceOrderService.id,
            part_id: part.id,
            notes: notes.trim()
        }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrderService.service_order_id}/remove_part`, remove_part).then((serviceOrder) => {
                setSaving(false)
                enqueueSnackbar('Successfully removed part', { variant: 'success' })
                closeModal(serviceOrder.data.data)
            }).catch((error) => {
                setSaving(false)
                enqueueSnackbar(`Failed to remove part <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    return (
        (<div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                maxWidth={'md'}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <LoadingBackdropIndeterminate
                    open={saving}
                    text={"Saving Part"}
                />
                <LoadingBackdropIndeterminate
                    open={isLoading}
                    text={"Searching Inventory"}
                />
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>{addingNewPart ? 'Adding' : 'Editing'} Part</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box>
                        {searchedPart &&
                            <Alert variant="filled" severity="info" action={
                                <Button color="inherit" size="small" onClick={() => { setSearchedPart(false); setMyPart(part || {}) }}>
                                    CANCEL
                                </Button>
                            }>
                                Imported data.
                            </Alert>
                        }
                        {searchedPart?.qoh !== undefined &&
                            <Alert variant="filled" severity={searchedPart.qoh === 0 ? "warning" : "info"} sx={{ mt: 1 }}>
                                Quantity On Hand: {searchedPart.qoh}
                            </Alert>
                        }
                        <Grid container spacing={2} sx={{ minWidth: '300px' }}>
                            <Grid container alignItems={'center'} spacing={1} size={6}>
                                <Grid size="grow">
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="name"
                                        label="Name"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myPart.name || ""}
                                        onChange={handleChange("name")}
                                        disabled={Boolean(searchedPart ?? false)}
                                    />
                                </Grid>
                                {findLocation(locations, serviceOrder.current_location_id)?.integrations?.PALADIN === undefined &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" onClick={searchName} disabled={Boolean(searchedPart || !myPart.name)} sx={{ p: 1, minWidth: 1 }}>
                                            <SearchIcon />
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1} size={6}>
                                <Grid size="grow">
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="sku"
                                        label="SKU"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myPart.sku || ""}
                                        onChange={handleChange("sku")}
                                        disabled={Boolean(searchedPart ?? false)}
                                    />
                                </Grid>
                                <Grid size="auto">
                                    <Button aria-label="search" variant="contained" onClick={searchSkuUpc} disabled={Boolean(searchedPart || !myPart.sku)} sx={{ p: 1, minWidth: 1 }}>
                                        <SearchIcon />
                                    </Button>
                                </Grid>
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1} size={12}>
                                <Grid size="grow">
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="description"
                                        label="Description"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myPart.description || ""}
                                        onChange={handleChange("description")}
                                        disabled={Boolean(searchedPart ?? false)}
                                    />
                                </Grid>
                                {findLocation(locations, serviceOrder.current_location_id)?.integrations?.PALADIN === undefined &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" onClick={searchDescription} disabled={Boolean(searchedPart || !myPart.description)} sx={{ p: 1, minWidth: 1 }}>
                                            <SearchIcon />
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                            <Grid size={6}>
                                <TextField
                                    margin="dense"
                                    id="quantity"
                                    label="Quantity"
                                    type="number"
                                    fullWidth
                                    variant="filled"
                                    value={(myPart.quantity ?? 1).toString()}
                                    onChange={handleChange("quantity")}
                                />
                            </Grid>
                            <Grid size={6}>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    id="cost"
                                    label="Price"
                                    type="number"
                                    fullWidth
                                    variant="filled"
                                    value={myPart.cost || 0}
                                    onChange={handleChange("cost")}
                                    disabled={Boolean(searchedPart ?? false)}
                                    slotProps={{
                                        input: {
                                            startAdornment: <InputAdornment position="start">
                                                {<AttachMoneyIcon />}
                                            </InputAdornment>,
                                        },
                                    }}
                                />
                                {overrideQuotePrice !== null &&
                                    <Tooltip title={`Delete existing quoted price`} placement="bottom" arrow>
                                        <FormControlLabel
                                            control={<Checkbox />}
                                            label={`Override Quote ($${moneyFormat(part.quoted_cost ?? 0)})`}
                                            checked={overrideQuotePrice}
                                            onChange={(event) => setOverrideQuotePrice(event.target.checked)}
                                        />
                                    </Tooltip>
                                }
                            </Grid>
                            <Grid size={12}>
                                <Divider />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    margin="dense"
                                    id="addservicenote"
                                    label="Notes (optional)"
                                    type="text"
                                    fullWidth
                                    variant="filled"
                                    multiline
                                    minRows={2}
                                    value={notes}
                                    onChange={(event) => setNotes(event.target.value)}
                                    sx={{ mt: 0 }}
                                    slotProps={{
                                        input: { maxLength: 2055 }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" sx={{ padding: "0px 30px 10px 30px", width: '100%' }}>
                        <Grid size="auto">
                            {!addingNewPart &&
                                <Button variant='outlined' color='error' onClick={removePart} sx={{ mr: 1 }}>
                                    <DeleteIcon /> <Box sx={{ pl: 1 }}><span>Remove</span></Box>
                                </Button>
                            }
                        </Grid>
                        <Grid size="auto">
                            <Button variant="contained" autoFocus onClick={addingNewPart ? create : save}>
                                {addingNewPart ? 'Add' : 'Update'}
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
            <InventorySearchModal
                searchedParts={searchedParts}
                onClose={(results) => {
                    if (results) {
                        searchedPartClick(results)
                    } else {
                        setSearchedParts([])
                    }
                }}
            />
        </div >)
    );
}

export function InventorySearchModal({ searchedParts, onClose }) {

    return (
        <Dialog
            open={searchedParts?.length > 0}
            onClose={() => onClose(false)}
            maxWidth={'lg'}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <DialogTitle>
                Inventory Search
                <IconButton
                    aria-label="close"
                    onClick={() => onClose(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ minWidth: '600px' }}>
                <DataGrid
                    slots={{
                        columnHeaders: () => null,
                    }}
                    rows={searchedParts ?? []}
                    columns={columns}
                    autoHeight={true}
                    onRowClick={(params) => onClose(params.row)}
                    hideFooter
                    disableColumnMenu
                    disableColumnSelector
                    disableColumnFilter
                />
            </DialogContent>
        </Dialog>
    )
}